/* iceberg-ui */
import { Flex } from '@auviknetworks/iceberg-ui';

/* MUI */
import { CircularProgress, Grid } from '@mui/material';

/* Types */
import { User } from 'application/Shared/types/user';

/* Hooks */
import { User as UserContext } from 'objects/User';

/* React */
import React, { useEffect, useState } from 'react';

import { useConnectionsAndRecipesContext } from './ConnectionsAndRecipesContext';

/* Components */
import { SaasOpsActionListAndForm } from './SaasOpsActionListAndForm';
import { SupportingDetailsSidePanel } from './SupportingDetailsSidePanel';
import { PAGE_HEADER_HEIGHT } from './constants';
import { sortAndBuildSaasOpsActionItemsState } from './functions';
import {
    AllowedActions,
    SaasOpsActionItemStatus,
    SaasOpsActionItemsState,
    SaasOpsActionStatus
} from './types';

/**
 * Determines the overall status of SaaS operations actions based on the state of tenant applications
 * @param {SaasOpsActionItemsState} saasOpsActionItemsState - Current state of tenant applications
 * @returns {SaasOpsActionStatus} Overall status of SaaS operations
 */
const getOverallSaasOpsActions = (
    saasOpsActionItemsState: SaasOpsActionItemsState
): SaasOpsActionStatus => {
    // If all apps are completed or removed, return completed
    if (
        saasOpsActionItemsState.every(
            (item) =>
                item.status === SaasOpsActionItemStatus.Completed ||
                item.status === SaasOpsActionItemStatus.Removed
        )
    ) {
        return SaasOpsActionStatus.Completed;
    }

    if (
        saasOpsActionItemsState.some(
            (item) => item.status === SaasOpsActionItemStatus.Error
        )
    ) {
        return SaasOpsActionStatus.Stopped;
    }

    return SaasOpsActionStatus.InProgress;
};

/**
 * Component that builds the initial state for SaaS operations actions
 * for a specific user and application.
 * @component
 * @param {Object} props - Component props
 * @param {string} props.applicationId - List of application IDs. Supported comma-separated IDs.
 * @param {Partial<User>} props.userDtails - details of the user to be onboarded or offboarded
 * @param {AllowedActions} props.action - Action to be performed on the applications (onboard or offboard)
 * @returns {JSX.Element | null} App list and form view with user details, or null if no tenant apps exist
 */
export const SaasOpsActionItemsStateBuilder = ({
    action,
    userDetails
}: {
    action: AllowedActions;
    userDetails: Partial<User>;
}): JSX.Element | null => {
    const {
        applicationIds,
        recipes,
        loading: loadingRecipes
    } = useConnectionsAndRecipesContext();

    const [saasOpsActionItemsState, setSaasOpsActionItemsState] =
        useState<SaasOpsActionItemsState>([]);

    const [currentAppId, setCurrentAppId] = useState('');

    useEffect(() => {
        if (!loadingRecipes && recipes.length) {
            const actionItemsState = sortAndBuildSaasOpsActionItemsState(
                recipes,
                applicationIds
            );
            setSaasOpsActionItemsState(actionItemsState);
            setCurrentAppId(actionItemsState?.[0]?.applicationId || '');
        }
    }, [loadingRecipes, recipes, applicationIds]);

    if (loadingRecipes) {
        return (
            <Flex justifyContent="center" mt={5}>
                <CircularProgress />
            </Flex>
        );
    }

    const overallSaasOpsActionStatus = getOverallSaasOpsActions(
        saasOpsActionItemsState
    );

    return saasOpsActionItemsState.length > 0 ? (
        <Grid
            container
            height={`calc(100vh - ${PAGE_HEADER_HEIGHT})`}
            spacing={0}
            flexWrap="nowrap"
            overflow="hidden"
        >
            <UserContext data={userDetails} id={userDetails?.id}>
                <Grid item flexGrow={1} p={3} sx={{ overflow: 'hidden' }}>
                    <SaasOpsActionListAndForm
                        action={action}
                        currentAppId={currentAppId}
                        overallSaasOpsActionStatus={overallSaasOpsActionStatus}
                        saasOpsActionItemsState={saasOpsActionItemsState}
                        setSaasOpsActionItemsState={setSaasOpsActionItemsState}
                        setCurrentAppId={setCurrentAppId}
                    />
                </Grid>
                <SupportingDetailsSidePanel
                    action={action}
                    saasOpsActionItemsState={saasOpsActionItemsState}
                    currentAppId={currentAppId}
                    overallSaasOpsActionStatus={overallSaasOpsActionStatus}
                />
            </UserContext>
        </Grid>
    ) : null;
};
